import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import './footer.css';


const Footer = () => {
  const { t, i18n } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [socialLinks, setSocialLinks] = useState({});

  useEffect(() => {
    try {
      const stored = localStorage.getItem("social_links");
      if (stored) setSocialLinks(JSON.parse(stored));
    } catch { /* ignore parse errors */ }
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const currentYear = new Date().getFullYear();

  // Determine the correct PDF file based on language
  const lang = i18n.language.startsWith("de")
    ? "de"
    : i18n.language.startsWith("es")
    ? "es"
    : "en";
  const houseRulesUrl = `/houserules/houserules_${lang}.pdf`;

  return (
    <footer className="mt-auto w-full bg-gray-900 text-white p-4 relative z-40">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-0 items-center w-full text-sm">
        {/* LEFT SIDE (links + language menu) */}
        <div className="flex items-center justify-center md:justify-start gap-4 md:gap-6 flex-wrap">
          <Link to="/contact-us" className="hover:underline">
            {t("contact_us")}
          </Link>

          <a
            href={houseRulesUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {t("house_rules")}
          </a>

          <Link to="/politica-de-privacidad" className="hover:underline">
            {t("privacy_policy")}
          </Link>

          <Link to="/condiciones-del-servicio" className="hover:underline">
            {t("terms_conditions")}
          </Link>

          <Link to="/solicitud-eliminacion-datos" className="hover:underline">
            {t("data_deletion_request")}
          </Link>

          <div className="relative inline-block text-left">
            <button onClick={toggleDropdown} className="hover:underline">
              {t("language")}
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 mb-2 w-32 bg-white text-black rounded-md shadow-lg dropdown-menu">
                <button
                  onClick={() => changeLanguage("en")}
                  className="block px-4 py-2 text-sm hover:bg-gray-200 w-full text-left"
                >
                  English
                </button>
                <button
                  onClick={() => changeLanguage("es")}
                  className="block px-4 py-2 text-sm hover:bg-gray-200 w-full text-left"
                >
                  Español
                </button>
                <button
                  onClick={() => changeLanguage("de")}
                  className="block px-4 py-2 text-sm hover:bg-gray-200 w-full text-left"
                >
                  Deutsch
                </button>
              </div>
            )}
          </div>
        </div>

        {/* CENTER (copyright) */}
        <div className="text-center text-xs">
          Copyright © {currentYear}{" "}
          <a href="http://guerra-und-ramos-se.de/">Guerra und Ramos - SE</a> –{" "}
          {t("all_rights_reserved")}.
        </div>

        {/* RIGHT SIDE (logo + text) */}
        <a
          href="https://guerra-und-ramos-se.de"
          className="flex items-center justify-center md:justify-end gap-2 text-xs md:text-sm"
        >
          {t("developed_by")}
          <img src="/static/images/SE-Logo.webp" alt="SE Logo" className="w-8 h-8" />
        </a>
      </div>

      {/* SOCIAL NETWORK ICONS */}
      {(socialLinks.instagram_url || socialLinks.twitter_url || socialLinks.facebook_url) && (
        <div className="flex justify-center gap-4 mt-3 mb-1">
          {socialLinks.instagram_url && (
            <a href={socialLinks.instagram_url} target="_blank" rel="noopener noreferrer" title="Instagram">
              <svg className="w-6 h-6 fill-current text-white hover:text-pink-400 transition" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </a>
          )}
          {socialLinks.twitter_url && (
            <a href={socialLinks.twitter_url} target="_blank" rel="noopener noreferrer" title="X / Twitter">
              <svg className="w-6 h-6 fill-current text-white hover:text-blue-400 transition" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
          )}
          {socialLinks.facebook_url && (
            <a href={socialLinks.facebook_url} target="_blank" rel="noopener noreferrer" title="Facebook">
              <svg className="w-6 h-6 fill-current text-white hover:text-blue-500 transition" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
          )}
        </div>
      )}

    </footer>

  );
};

export default Footer;